// GroupTarifs.js
import React, { useState } from 'react';
import { Button } from 'react-bootstrap';

import DatePickerCalendar from './components/DatePicker/datepickercalendar';
import { dictionary_default } from './data';

const ageCategoryLabel = (c) => c ? `від ${c.split("_").join(" до ")} років` : '';

const getRow = (row, i) => (
  { 
    id: row._id || `temp-${i}`,
    number: i + 1, 
    price: { value: row.averageForDay, name: row.averageForDay + " грн." }, 
    startDate: { value: new Date(row.dateFrom).toLocaleDateString('uk') }, 
    endDate: { value: row.dateTo ? new Date(row.dateTo).toLocaleDateString('uk') : 'Постійний' },
    ageCategory: { value: row.ageLimit, name: ageCategoryLabel(row.ageLimit) }, 
    type: { value: row.typeKitchen, name: dictionary_default.typeKitchen[row.typeKitchen || "standart"] },
    tariffType: row.dateTo ? 'temporary' : 'permanent',
    rawData: row
  }
);

const getDefaultObj = () => ({
  dateFrom: new Date().toISOString(),
  dateTo: null,
  averageForDay: null,
  ageLimit: '1_4',
  typeKitchen: 'standart'
});

const GroupTariffs = ({ formData, onChange, onDelete, mode = "edit" }) => {
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [editingTariff, setEditingTariff] = useState(null);
  const [newTariff, setNewTariff] = useState(getDefaultObj());

  const isViewMode = mode === "view";

  const validateTariff = (tariff) => {
    if (!tariff.averageForDay || tariff.averageForDay <= 0) {
      return 'Вкажіть вартість тарифу';
    }
    if (!tariff.dateFrom) {
      return 'Вкажіть дату початку дії';
    }
    if (!tariff.ageLimit) {
      return 'Виберіть вікову категорію';
    }
    if (!tariff.typeKitchen) {
      return 'Виберіть тип харчування';
    }
    if (tariff.tariffType === 'temporary' && !tariff.dateTo) {
      return 'Для тимчасового тарифу вкажіть дату закінчення';
    }
    if (tariff.tariffType === 'temporary' && tariff.dateTo && new Date(tariff.dateTo) <= new Date(tariff.dateFrom)) {
      return 'Дата закінчення повинна бути пізніше дати початку';
    }
    return '';
  };

  const handleEditTariff = (tariff) => {
    if (isViewMode) return;
    
    setEditingTariff(tariff);
    setNewTariff({
      ...tariff.rawData,
      tariffType: tariff.rawData.dateTo ? 'temporary' : 'permanent'
    });
    setShowAddPanel(true);
  };

  const handleDeleteTariff = (tariffId) => {
    if (isViewMode) return;
    
    if (onDelete) {
      onDelete(tariffId);
    }
  };

  const handleAddOrUpdateTariff = () => {
    if (isViewMode) return;
    
    const validationError = validateTariff(newTariff);
    if (validationError) {
      alert(validationError);
      return;
    }

    const tariffToSend = { ...newTariff };
    
    if (tariffToSend.tariffType === 'permanent') {
      delete tariffToSend.dateTo;
    }
    
    delete tariffToSend.tariffType;

    setShowAddPanel(false);
    setEditingTariff(null);
    setNewTariff(getDefaultObj());
    
    if (onChange) {
      onChange(tariffToSend, editingTariff?.id);
    }
  };

  const handleInputChange = (field, value) => {
    if (isViewMode) return;
    
    setNewTariff(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'tariffType' && value === 'permanent') {
      setNewTariff(prev => ({
        ...prev,
        dateTo: null
      }));
    }
  };

  const handleCancel = () => {
    if (isViewMode) return;
    
    setShowAddPanel(false);
    setEditingTariff(null);
    setNewTariff(getDefaultObj());
  };

  const price = formData?.price || [];

  return (
    <div className="section">
      <div className="table-bordered-wrap tariffs-block">
        <table className="table tariffs-table mb-0">
          <thead>
            <tr>
              <th style={{ width: '48px' }}>№</th>
              <th>Вартість</th>
              <th>Початок дії</th>
              <th>Закінчення дії</th>
              <th>Вікова категорія</th>
              <th>Тип харчування</th>
              {!isViewMode && (
                <th className="text-end" style={{ width: '96px' }}>Дії</th>
              )}
            </tr>
          </thead>
          <tbody>
            {price.map((p, i) => getRow(p, i)).map((tariff) => (
              <tr key={tariff.id}>
                <td>{tariff.number}</td>
                <td>{tariff.price.name}</td>
                <td>{tariff.startDate.value}</td>
                <td>{tariff.endDate.value}</td>
                <td>{tariff.ageCategory.name}</td>
                <td>{tariff.type.name}</td>
                {!isViewMode && (
                  <td className="text-end">
                    <button 
                      className="btn-icon"
                      onClick={() => handleEditTariff(tariff)}
                      title="Редагувати"
                    >
                      <i className="fa-solid fa-pen"></i>
                    </button>
                    <button 
                      className="btn-icon"
                      onClick={() => handleDeleteTariff(tariff.id)}
                      title="Видалити"
                    >
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {showAddPanel && !isViewMode ? (
          <div className="panel add-tariff-panel">
            <h6>{editingTariff ? 'Редагувати тариф харчування' : 'Додати тариф харчування'}</h6>
            <div className="panel-inner">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Тип тарифа</label>
                  <select
                    className="form-select"
                    value={newTariff.tariffType}
                    onChange={(e) => handleInputChange('tariffType', e.target.value)}
                  >
                    <option value="permanent">Постійний</option>
                    <option value="temporary">Тимчасовий</option>
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Дата початку дії</label>
                  <DatePickerCalendar 
                    dateFormat="dd.MM.YYYY" 
                    value={newTariff.dateFrom || null} 
                    onChange={(value) => handleInputChange('dateFrom', value)} 
                  />
                </div>

                {newTariff.tariffType === 'temporary' && (
                  <div className="col-md-3">
                    <label className="form-label">Дата закінчення дії</label>
                    <DatePickerCalendar 
                      dateFormat="dd.MM.YYYY" 
                      value={newTariff.dateTo || null} 
                      onChange={(value) => handleInputChange('dateTo', value)}
                      minDate={new Date(newTariff.dateFrom)}
                    />
                  </div>
                )}

                <div className="col-md-3">
                  <label className="form-label">Тариф</label>
                  <input  
                    type="number"
                    className="form-control"
                    value={parseFloat(newTariff.averageForDay)}
                    onChange={(e) => handleInputChange('averageForDay', e.target.value || 0)}
                    placeholder="0,00 грн"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Вікова категорія</label>
                  <select
                    className="form-select"
                    value={newTariff.ageLimit}
                    onChange={(e) => handleInputChange('ageLimit', e.target.value)}
                  >
                    {dictionary_default.ageCategoriesKitchen.map((c, index) => (
                      <option key={index} value={c}>
                        {ageCategoryLabel(c)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Тип харчування</label>
                  <select
                    className="form-select"
                    value={newTariff.typeKitchen}
                    onChange={(e) => handleInputChange('typeKitchen', e.target.value)}
                  >
                    {Object.keys(dictionary_default.typeKitchen).map((c, index) => (
                      <option key={index} value={c}>
                        {dictionary_default.typeKitchen[c]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="actions d-flex justify-content-between align-items-center mt-3">
                <Button variant="primary" onClick={handleAddOrUpdateTariff}>
                  {editingTariff ? 'Оновити тариф' : 'Додати тариф'}
                </Button>
                <a href="#" className="link-sm" onClick={handleCancel}>
                  Скасувати
                </a>
              </div>
            </div>
          </div>
        ) : (
          !isViewMode && (
            <div className="panel-actions d-flex justify-content-end mt-2">
              <a href="#" className="link-sm" onClick={() => setShowAddPanel(true)}>
                <i className="fa-solid fa-plus me-1"></i>Додати тариф
              </a>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default GroupTariffs;